import { resolveCatalogCacheTtlMs, writeCachedModels } from "../cache/modelCache.js";
import {
  matchModelInAiProfiles,
  type AiProfilesModelMatch,
  type MatchModelInAiProfilesOptions,
} from "../cost/aiProfilesMatch.js";
import { TEXT_CATALOG_LANE } from "../profiles/catalogLane.js";
import type { AiModelRecord } from "../models/types.js";
import { ModelNameResolver } from "../sync/modelNameResolver/ModelNameResolver.js";
import { normalizeProvider, normalizeString } from "../sync/modelNameResolver/normalize.js";
import {
  isEffectiveOpenRouterTransport,
  loadOpenRouterRoutingEnv,
} from "../sync/openRouterRoutingEnv.js";
import type {
  ModelResolutionInput,
  ModelResolutionResult,
  ModelResolutionSuccess,
  ModelResolverOptions,
  ResolutionStrategy,
} from "../sync/modelNameResolver/types.js";
import { isKnownProfileInput } from "@x12i/ai-profiles";
import {
  invalidateCatalogLoadCache,
  loadCatalogSourcesCached,
} from "./catalogLoadCache.js";
import type { LoadCatalogOptions } from "./loadCatalogSources.js";

export type AiModelsCatalogClientOptions = LoadCatalogOptions & {
  cacheTtlMs?: number;
  /** Cache scope key (default: `default`). */
  cacheKey?: string;
  resolverOptions?: ModelResolverOptions;
};

function isOpenRouterProvider(provider?: string): boolean {
  return normalizeProvider(provider) === "openrouter";
}

function profileResolutionStrategy(
  matchedVia: AiProfilesModelMatch["matchedVia"],
): ResolutionStrategy {
  return matchedVia === "profile" ? "ai-profiles-profile" : "ai-profiles-model-id";
}

export class AiModelsCatalogClient {
  private readonly cacheTtlMs: number;
  private readonly cacheKey: string;
  private readonly loadOptions: LoadCatalogOptions;
  private readonly resolverOptions?: ModelResolverOptions;

  private directModels: Map<string, AiModelRecord> | null = null;
  private openRouterModels: Map<string, AiModelRecord> | null = null;
  private loadedAt = 0;
  private loadPromise: Promise<void> | null = null;

  constructor(options: AiModelsCatalogClientOptions = {}) {
    this.cacheTtlMs = resolveCatalogCacheTtlMs(options.cacheTtlMs);
    this.cacheKey = options.cacheKey ?? "default";
    this.loadOptions = {
      source: options.source,
      bundledOnly: options.bundledOnly,
      directCatalogUrl: options.directCatalogUrl,
      openRouterCatalogUrl: options.openRouterCatalogUrl,
      fetchTimeoutMs: options.fetchTimeoutMs,
    };
    this.resolverOptions = options.resolverOptions;
  }

  private isInstanceCacheValid(): boolean {
    return (
      this.directModels !== null &&
      this.openRouterModels !== null &&
      Date.now() - this.loadedAt < this.cacheTtlMs
    );
  }

  private applyLoaded(
    direct: Map<string, AiModelRecord>,
    openrouter: Map<string, AiModelRecord>,
  ): void {
    this.directModels = direct;
    this.openRouterModels = openrouter;
    this.loadedAt = Date.now();
    writeCachedModels(`${this.cacheKey}:direct`, direct, this.cacheTtlMs);
    writeCachedModels(`${this.cacheKey}:openrouter`, openrouter, this.cacheTtlMs);
  }

  private async ensureLoaded(forceRefresh = false): Promise<void> {
    if (!forceRefresh && this.isInstanceCacheValid()) return;

    if (this.loadPromise) return this.loadPromise;

    this.loadPromise = (async () => {
      const loaded = await loadCatalogSourcesCached(this.loadOptions, {
        cacheKey: this.cacheKey,
        ttlMs: this.cacheTtlMs,
        forceRefresh,
      });
      this.applyLoaded(loaded.direct, loaded.openrouter);
    })().finally(() => {
      this.loadPromise = null;
    });

    await this.loadPromise;
  }

  mergedModels(): Map<string, AiModelRecord> {
    const map = new Map<string, AiModelRecord>();
    for (const r of this.directModels?.values() ?? []) {
      map.set(r.modelId, r);
    }
    for (const r of this.openRouterModels?.values() ?? []) {
      map.set(r.modelId, r);
    }
    return map;
  }

  private catalogForProvider(provider?: string): Map<string, AiModelRecord> {
    if (isOpenRouterProvider(provider)) {
      return this.openRouterModels ?? new Map();
    }
    return this.directModels ?? new Map();
  }

  private resolver(
    models: Map<string, AiModelRecord>,
    options?: ModelResolverOptions,
  ): ModelNameResolver {
    return new ModelNameResolver(models, { ...this.resolverOptions, ...options });
  }

  async getAllModels(): Promise<Map<string, AiModelRecord>> {
    await this.ensureLoaded();
    return this.mergedModels();
  }

  async getDirectModels(): Promise<Map<string, AiModelRecord>> {
    await this.ensureLoaded();
    return new Map(this.directModels);
  }

  async getOpenRouterModels(): Promise<Map<string, AiModelRecord>> {
    await this.ensureLoaded();
    return new Map(this.openRouterModels);
  }

  async resolveModel(
    input: ModelResolutionInput,
    options?: ModelResolverOptions,
  ): Promise<ModelResolutionResult> {
    await this.ensureLoaded();

    const mergedOptions = { ...this.resolverOptions, ...options };

    // Local provider passthrough before profile/catalog (handled inside ModelNameResolver).
    const provider = normalizeProvider(input.provider);
    if (provider) {
      const localPass = this.resolver(new Map(), mergedOptions).resolve(input);
      if (localPass.found && localPass.resolvedVia.includes("local-provider-passthrough")) {
        return localPass;
      }
    }

    // Profile-first: known profile input before any catalog fuzzy match.
    if (isKnownProfileInput(input.model.trim())) {
      const viaProfile = await this.resolveViaProfile(input, mergedOptions);
      if (viaProfile) return viaProfile;
    }

    let result = this.resolveFromCatalogMaps(input, mergedOptions);

    if (!result.found) {
      const viaProfile = await this.resolveViaProfile(input, mergedOptions);
      if (viaProfile) return viaProfile;
      return result;
    }

    return this.alignForOpenRouterTransport(result, input);
  }

  private resolveFromCatalogMaps(
    input: ModelResolutionInput,
    options?: ModelResolverOptions,
  ): ModelResolutionResult {
    const primary = this.catalogForProvider(input.provider);
    const secondary = isOpenRouterProvider(input.provider)
      ? (this.directModels ?? new Map())
      : (this.openRouterModels ?? new Map());

    let result = this.resolver(primary, options).resolve(input);
    if (result.found && result.record) {
      return this.alignForOpenRouterTransport(result, input);
    }

    if (secondary.size > 0) {
      const fallback = this.resolver(secondary, options).resolve(input);
      if (fallback.found && fallback.record) {
        return this.alignForOpenRouterTransport(fallback, input);
      }
      if (!result.found && fallback.found) result = fallback;
    }

    if (!result.found) {
      result = this.resolver(this.mergedModels(), options).resolve(input);
    }

    if (result.found) {
      return this.alignForOpenRouterTransport(result, input);
    }

    return result;
  }

  private alignForOpenRouterTransport(
    result: ModelResolutionSuccess,
    input: ModelResolutionInput,
  ): ModelResolutionSuccess {
    if (!result.routedViaOpenRouter) return result;

    let { modelId, record } = result;
    if (!modelId.includes("/")) {
      const vendor =
        record?.providerId ??
        normalizeProvider(input.provider) ??
        undefined;
      if (vendor && vendor !== "openrouter") {
        modelId = `${vendor}/${modelId}`;
      }
    }

    const orRecord = this.openRouterModels?.get(modelId);
    if (orRecord) {
      record = orRecord;
    }

    if (modelId === result.modelId && record === result.record) return result;
    return { ...result, modelId, record };
  }

  private async resolveViaProfile(
    input: ModelResolutionInput,
    options?: ModelResolverOptions,
  ): Promise<ModelResolutionSuccess | null> {
    const profileOpts: MatchModelInAiProfilesOptions = {
      catalogLane: options?.catalogLane ?? TEXT_CATALOG_LANE,
    };
    const profileMatch = await matchModelInAiProfiles(
      input.model,
      input.provider,
      profileOpts,
    );
    if (!profileMatch) return null;

    const strategy = profileResolutionStrategy(profileMatch.matchedVia);
    const attempts: ModelResolutionInput[] = [];
    const seen = new Set<string>();
    const add = (model: string, provider?: string) => {
      const key = `${provider ?? ""}\0${normalizeString(model)}`;
      if (seen.has(key)) return;
      seen.add(key);
      attempts.push({ model, provider });
    };

    add(profileMatch.canonicalModelId, input.provider ?? profileMatch.provider);
    add(profileMatch.canonicalModelId, profileMatch.provider);
    if (!isOpenRouterProvider(input.provider)) {
      add(profileMatch.canonicalModelId, "openrouter");
    }

    let catalogResult: ModelResolutionResult | null = null;
    for (const attempt of attempts) {
      const result = this.resolveFromCatalogMaps(attempt, options);
      catalogResult = result;
      if (result.found && result.record) break;
    }

    const normalisedInput = normalizeString(input.model);
    if (!catalogResult?.found) {
      const routingEnv = options?.routingEnv ?? loadOpenRouterRoutingEnv();
      const routedViaOpenRouter = isEffectiveOpenRouterTransport(routingEnv, {
        provider: input.provider ?? profileMatch.provider,
        modelId: profileMatch.canonicalModelId,
        routeViaOpenRouter: options?.routeViaOpenRouter,
      });

      const aligned = this.alignForOpenRouterTransport(
        {
          found: true,
          modelId: profileMatch.canonicalModelId,
          record: null,
          routedViaOpenRouter,
          confidence: 0.85,
          resolvedVia: [strategy],
          resolvedReason: `Resolved profile "${input.model}" → ${profileMatch.canonicalModelId} via ai-profiles (${profileMatch.matchedVia})`,
          normalisedInput,
          profile: profileMatch.profile,
          choice: profileMatch.choice,
        },
        { model: profileMatch.canonicalModelId, provider: profileMatch.provider },
      );

      return {
        ...aligned,
        resolvedVia: [strategy],
        profile: profileMatch.profile,
        choice: profileMatch.choice,
      };
    }

    const viaProfile = catalogResult as ModelResolutionSuccess;
    return {
      ...viaProfile,
      resolvedVia: [strategy, ...viaProfile.resolvedVia],
      resolvedReason: `Resolved profile "${input.model}" → ${viaProfile.modelId} via ai-profiles (${profileMatch.matchedVia})`,
      normalisedInput,
      profile: profileMatch.profile,
      choice: profileMatch.choice,
    };
  }

  async getModel(
    modelId: string,
    provider?: string,
    options?: ModelResolverOptions,
  ): Promise<AiModelRecord | null> {
    const result = await this.resolveModel({ model: modelId, provider }, options);
    return result.found ? result.record : null;
  }

  /** Clear caches and fetch catalogs again immediately. */
  async refresh(): Promise<void> {
    invalidateCatalogLoadCache(this.cacheKey);
    this.directModels = null;
    this.openRouterModels = null;
    this.loadedAt = 0;
    this.loadPromise = null;
    await this.ensureLoaded(true);
  }
}
